import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import addImage from '../../images/tag/add.svg';
import editImage from '../../images/tag/mode_edit.svg';
import * as actionTypes from '../../actions/actionTypes';
import {
  Main,
  NavigationBlock,
  Depth,
  Body,
  List,
  ListItem,
  UnavailableDiv,
  TagName,
  EditButton,
  AddButton,
} from './styles/MasterListsStyles';
import useIsAddDisabled from './hooks/useIsAddEnabled';
import selectTagSetAndGetFirstLevelTags from './functions/selectTagSetAndGetFirstLevelTags';
import selectTagAndGetNextLevelTags from './functions/selectTagAndGetNextLevelTags';
import useIsEditDisabled from './hooks/useIsEditDisabled';
import getCurrentLevelTags from './functions/getCurrentLevelTags';

/**
 * 各階層のタグ項目を表示するコンポーネント
 * タグを選択した際にAPIを叩いて下階層のタグを取得する
 *
 * @function
 * @param {number} depth - 作成する表の階層(例： 階層1)
 * @param {string} selectedId - 選択されたタグのID
 * @param {Object} list - 表示するタグの情報
 * @param {Function} dispatch - Redux dispatch関数
 * @returns {ReactElement}
 */
const createList = (
  depth,
  selectedTagSetId,
  selectedTagIds,
  list,
  dispatch,
  apiToken,
) => {
  let isSelected;
  if (depth === 0) {
    isSelected = selectedTagSetId === list.id;
  } else {
    isSelected = selectedTagIds[depth - 1] === list.id;
  }

  const handleOnClick = () => {
    if (depth === 0) {
      dispatch(selectTagSetAndGetFirstLevelTags(apiToken, list.id));
    } else if (depth >= 1 && depth <= 5) {
      dispatch(selectTagAndGetNextLevelTags(apiToken, depth, list.id));
    }
  };

  const key = `masterList${list.id}`;
  return (
    <ListItem
      key={key}
      id="tagButton"
      selected={isSelected}
      role="presentation"
      onClick={handleOnClick}
    >
      {!list.isUsable && (
        <UnavailableDiv>
          <FormattedMessage id="master.MasterLists.unavailable" />
        </UnavailableDiv>
      )}
      <TagName>{list.name}</TagName>
    </ListItem>
  );
};

const DepthIndicator = ({ currentDepth }) => {
  if (currentDepth === 0) {
    return <FormattedMessage id="master.MasterLists.item.tagSet" />;
  }

  return (
    <>
      <FormattedMessage id="master.MasterLists.item" /> {currentDepth}
    </>
  );
};

DepthIndicator.propTypes = {
  currentDepth: PropTypes.number.isRequired,
};

/**
 * タグセット・タグの各階層を表示するコンポーネント。
 * 選択された親タグセット・タグに紐づくタグを表示する
 *
 * @function
 * @param {Object} props - MasterListsのprops
 * @param {number} props.currentDepth - 表示する階層。0の場合はタグセットとなる。
 * @param {boolean} props.isCompanyAdminUser - 現在のログインユーザーが企業管理者かどうかの真偽値
 * @returns {ReactElement}
 */
const MasterLists = ({ currentDepth, isCompanyAdminUser }) => {
  const dispatch = useDispatch();
  const {
    tagSets,
    selectedTagSetId,
    selectedTagIds,
    apiToken,
    tags,
    showUnavailableTags,
  } = useSelector(state => ({
    tagSets: state.master.companyAssociatedTagSets,
    selectedTagSetId: state.master.selectedTagSetId,
    selectedTagIds: state.master.selectedTagIds,
    apiToken: state.main.apiToken,
    tags: state.master.tags,
    showUnavailableTags: state.master.showUnavailableTags,
  }));

  const currentLevelTags = getCurrentLevelTags(currentDepth, tagSets, tags);

  const isEditDisabled = useIsEditDisabled(
    currentDepth,
    selectedTagIds,
    selectedTagSetId,
    tags,
  );
  const isAddDisabled = useIsAddDisabled(
    currentDepth,
    selectedTagIds,
    selectedTagSetId,
    isCompanyAdminUser,
  );

  const handleEditButton = () => {
    let item;
    if (currentDepth === 0) {
      item = currentLevelTags.find(list => list.id === selectedTagSetId);
    } else {
      item = currentLevelTags.find(
        list => list.id === selectedTagIds[currentDepth - 1],
      );
    }

    dispatch({
      type: actionTypes.OPEN_EDIT_MASTER_DIALOG,
      selectedTag: {
        currentDepth,
        id: item.id,
        name: item.name,
        parentId: item.parentId,
        isUsable: item.isUsable,
      },
    });
  };

  const handleAddButton = () => {
    let parentId;
    if (currentDepth === 0) {
      parentId = null;
    } else if (currentDepth === 1) {
      parentId = selectedTagSetId;
    } else {
      parentId = selectedTagIds[currentDepth - 2];
    }
    dispatch({
      type: actionTypes.OPEN_ONE_LAYER_MASTER_IMPORT_DIALOG,
      selectedTag: {
        depth: currentDepth,
        parentId,
      },
    });
  };

  // コンポーネントマウント時に最初のタグセットを選択する
  useEffect(() => {
    if (currentDepth === 0 && selectedTagSetId === '' && tagSets.length > 0) {
      dispatch(selectTagSetAndGetFirstLevelTags(apiToken, tagSets[0].id));
    }
  }, [apiToken, currentDepth, dispatch, selectedTagSetId, tagSets]);

  return (
    <Main className="masterTitle">
      <NavigationBlock>
        <Depth>
          <DepthIndicator currentDepth={currentDepth} />
        </Depth>
        {isCompanyAdminUser && (
          <>
            <EditButton
              id="masterEditButton"
              src={editImage}
              alt="editButton"
              disabled={isEditDisabled}
              onClick={handleEditButton}
            />
            <AddButton
              id="masterAddButton"
              src={addImage}
              alt="addButton"
              disabled={isAddDisabled}
              onClick={handleAddButton}
            />
          </>
        )}
      </NavigationBlock>
      <Body>
        {currentLevelTags ? (
          <List>
            {currentLevelTags.map(list => {
              if (!showUnavailableTags && !list.isUsable) {
                return null;
              }
              return createList(
                currentDepth,
                selectedTagSetId,
                selectedTagIds,
                list,
                dispatch,
                apiToken,
                showUnavailableTags,
              );
            })}
          </List>
        ) : (
          <FormattedMessage id="master.MasterLists.unselected" />
        )}
      </Body>
    </Main>
  );
};

MasterLists.propTypes = {
  currentDepth: PropTypes.number.isRequired,
  isCompanyAdminUser: PropTypes.bool.isRequired,
};

export default injectIntl(MasterLists);
